import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShoppingCart, 
  Search, 
  Filter, 
  RefreshCw, 
  Eye, 
  Edit, 
  Trash2, 
  Plus,
  Calendar,
  MapPin,
  Euro,
  Clock,
  User,
  Building,
  CheckCircle,
  XCircle,
  AlertCircle,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { commandeService } from '../services/commandeService';
import { formatDate, formatCurrency } from '../utils/formatters';
import CommandeModal from '../components/CommandeModal';
import CommandeViewModal from '../components/CommandeViewModal';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const Commandes = () => {
  // États principaux
  const [commandes, setCommandes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("tous");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // États pour les actions
  const [selectedCommandes, setSelectedCommandes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [commandeToEdit, setCommandeToEdit] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [commandeToView, setCommandeToView] = useState(null);

  // Charger les commandes
  const loadCommandes = async () => {
    try {
      setIsLoading(true);
      const data = await commandeService.getClientCommandes();
      setCommandes(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des commandes:', error);
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCommandes();
  }, []);

  // Actualiser les données
  const handleRefresh = () => {
    loadCommandes();
    toast.success('Données actualisées');
  };

  // Fonction pour obtenir le statut d'une commande
  const getCommandeStatus = (commande) => {
    if (commande.status === 'annulée') return 'annule';
    if (commande.status === 'terminée') return 'termine';
    return 'en_cours';
  };

  // Fonction pour obtenir le label du statut
  const getStatusLabel = (status) => {
    switch (status) {
      case 'en_cours': return 'En cours';
      case 'termine': return 'Terminée';
      case 'annule': return 'Annulée';
      default: return 'En cours';
    }
  };

  // Filtrer les commandes
  const filteredCommandes = commandes.filter(commande => {
    const matchesSearch = 
      commande.qualification?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      commande.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      commande.town?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "tous" || getCommandeStatus(commande) === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredCommandes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCommandes = filteredCommandes.slice(startIndex, startIndex + itemsPerPage);

  // Statistiques
  const stats = {
    total: commandes.length,
    en_cours: commandes.filter(c => getCommandeStatus(c) === 'en_cours').length,
    termine: commandes.filter(c => getCommandeStatus(c) === 'termine').length,
    annule: commandes.filter(c => getCommandeStatus(c) === 'annule').length
  };

  // Gérer la sélection multiple
  const handleSelectCommande = (commandeId) => {
    setSelectedCommandes(prev => 
      prev.includes(commandeId) 
        ? prev.filter(id => id !== commandeId)
        : [...prev, commandeId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCommandes.length === paginatedCommandes.length) {
      setSelectedCommandes([]);
    } else {
      setSelectedCommandes(paginatedCommandes.map(c => c._id));
    }
  };

  // Actions sur les commandes
  const handleViewCommande = (commande) => {
    setCommandeToView(commande);
    setIsViewModalOpen(true);
  };

  const handleEditCommande = (commande) => {
    setCommandeToEdit(commande);
    setIsModalOpen(true);
  };

  const handleDeleteCommande = async (commandeId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) {
      return;
    }

    try {
      await commandeService.deleteCommande(commandeId);
      setCommandes(prev => prev.filter(c => c._id !== commandeId));
      toast.success('Commande supprimée avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression de la commande');
    }
  };

  const handleCreateCommande = () => {
    setCommandeToEdit(null);
    setIsModalOpen(true);
  };

  const handleSaveCommande = (savedCommande) => {
    if (commandeToEdit) {
      // Modification
      setCommandes(prev => prev.map(c =>
        c._id === savedCommande._id ? savedCommande : c
      ));
    } else {
      // Création
      setCommandes(prev => [savedCommande, ...prev]);
    }
    setIsModalOpen(false);
    setCommandeToEdit(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <motion.div
        className="max-w-7xl mx-auto space-y-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* En-tête */}
        <motion.div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" variants={itemVariants}>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <ShoppingCart size={32} className="text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Gestion des Commandes</h1>
                <p className="text-slate-600">Suivez et gérez vos commandes de recrutement</p>
              </div>
            </div>
            <button
              onClick={handleCreateCommande}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Plus size={18} />
              <span>Nouvelle commande</span>
            </button>
          </div>

          {/* Statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <ShoppingCart size={20} className="text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                  <p className="text-sm text-slate-600">Total</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-800">{stats.en_cours}</p>
                  <p className="text-sm text-blue-600">En cours</p>
                </div>
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-800">{stats.termine}</p>
                  <p className="text-sm text-green-600">Terminées</p>
                </div>
              </div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle size={20} className="text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-800">{stats.annule}</p>
                  <p className="text-sm text-red-600">Annulées</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Filtres et recherche */}
        <motion.div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" variants={itemVariants}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher par qualification, client, ville..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Filter size={18} className="text-slate-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="tous">Tous les statuts</option>
                  <option value="en_cours">En cours</option>
                  <option value="termine">Terminées</option>
                  <option value="annule">Annulées</option>
                </select>
              </div>
            </div>

            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw size={18} className={isLoading ? "animate-spin" : ""} />
              <span>Actualiser</span>
            </button>
          </div>
        </motion.div>

        {/* Liste des commandes */}
        <motion.div className="bg-white rounded-xl shadow-sm border border-slate-200" variants={itemVariants}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-3 text-slate-600">Chargement des commandes...</span>
            </div>
          ) : filteredCommandes.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart size={48} className="mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">Aucune commande trouvée</h3>
              <p className="text-slate-500">
                {searchTerm || statusFilter !== "tous"
                  ? "Aucune commande ne correspond à vos critères de recherche."
                  : "Vous n'avez pas encore de commandes."
                }
              </p>
            </div>
          ) : (
            <div>
              {/* En-tête des colonnes */}
              <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
                <div className="grid grid-cols-12 gap-4 items-center text-sm font-medium text-slate-600">
                  <div className="col-span-1">
                    <input
                      type="checkbox"
                      checked={selectedCommandes.length === paginatedCommandes.length && paginatedCommandes.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>
                  <div className="col-span-3">Commande</div>
                  <div className="col-span-2">Client</div>
                  <div className="col-span-2">Dates</div>
                  <div className="col-span-1">Taux</div>
                  <div className="col-span-2">Lieu</div>
                  <div className="col-span-1">Actions</div>
                </div>
              </div>

              {/* Liste des commandes */}
              <div className="divide-y divide-slate-200">
                <AnimatePresence>
                  {paginatedCommandes.map((commande, index) => (
                    <motion.div
                      key={commande._id}
                      className="p-6 hover:bg-slate-50 transition-colors"
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      transition={{ delay: index * 0.05 }}
                    >
                      {/* Grille avec colonnes fixes et bien alignées */}
                      <div className="grid grid-cols-12 gap-4 items-center w-full">
                        {/* Checkbox - 1 colonne */}
                        <div className="col-span-1">
                          <input
                            type="checkbox"
                            checked={selectedCommandes.includes(commande._id)}
                            onChange={() => handleSelectCommande(commande._id)}
                            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                        </div>

                        {/* Commande - 3 colonnes */}
                        <div className="col-span-3 flex items-center space-x-3 min-w-0">
                          <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                            <Briefcase size={20} className="text-blue-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-slate-800 truncate">{commande.qualification || "Qualification non définie"}</p>
                            <p className="text-sm text-slate-500 truncate">{commande.typeContrat || "Type non défini"}</p>
                          </div>
                        </div>

                        {/* Client - 2 colonnes */}
                        <div className="col-span-2 flex items-center space-x-3 min-w-0">
                          <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                            <Building size={18} className="text-purple-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-slate-800 text-sm truncate">{commande.clientName || "Client non défini"}</p>
                            <p className="text-xs text-slate-500 truncate">{commande.contact || "Contact non disponible"}</p>
                          </div>
                        </div>

                        {/* Dates - 2 colonnes */}
                        <div className="col-span-2 flex items-center space-x-3 min-w-0">
                          <div className="p-2 bg-indigo-100 rounded-lg flex-shrink-0">
                            <Calendar size={18} className="text-indigo-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-slate-800 text-sm">{formatDate(commande.startDate)}</p>
                            <p className="text-xs text-slate-500">Fin: {formatDate(commande.endDate)}</p>
                          </div>
                        </div>

                        {/* Taux - 1 colonne */}
                        <div className="col-span-1 flex items-center space-x-2 min-w-0">
                          <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
                            <Euro size={16} className="text-emerald-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-slate-800 text-sm">
                              {commande.hourlyRate ? `${commande.hourlyRate}€` : "N/A"}
                            </p>
                            <p className="text-xs text-slate-500">/h</p>
                          </div>
                        </div>

                        {/* Lieu - 2 colonnes */}
                        <div className="col-span-2 flex items-center space-x-3 min-w-0">
                          <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                            <MapPin size={16} className="text-amber-600" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-slate-800 text-sm truncate">{commande.town || "Ville non définie"}</p>
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              getCommandeStatus(commande) === "termine"
                                ? "bg-green-100 text-green-800"
                                : getCommandeStatus(commande) === "annule"
                                ? "bg-red-100 text-red-800"
                                : "bg-blue-100 text-blue-800"
                            }`}>
                              {getStatusLabel(getCommandeStatus(commande))}
                            </div>
                          </div>
                        </div>

                        {/* Actions - 1 colonne */}
                        <div className="col-span-1 flex items-center justify-end space-x-1">
                          <button
                            onClick={() => handleViewCommande(commande)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Voir la commande"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleEditCommande(commande)}
                            className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                            title="Modifier la commande"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteCommande(commande._id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Supprimer la commande"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-600">
                  Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredCommandes.length)} sur {filteredCommandes.length} commandes
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Précédent
                  </button>
                  <span className="px-3 py-1 text-sm text-slate-600">
                    Page {currentPage} sur {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Modal de création/édition */}
        <CommandeModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setCommandeToEdit(null);
          }}
          commande={commandeToEdit}
          onSave={handleSaveCommande}
        />

        {/* Modal de visualisation */}
        <CommandeViewModal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setCommandeToView(null);
          }}
          commande={commandeToView}
        />
      </motion.div>
    </div>
  );
};

export default Commandes;
